import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { HeroSlider } from "../components/HeroSlider";
import { StatsRibbon } from "../components/StatsRibbon";
import { Marquee } from "../components/Marquee";
import { Footer } from "../components/Footer";
import { useLang } from "../i18n/LanguageContext";
import { productImages } from "../data/images";

const TEASER_CARDS = [
  { to: "/products", key: "nav.products", img: productImages.parquet, idx: "01" },
  { to: "/services", key: "nav.services", img: productImages.design, idx: "02" },
  { to: "/projects", key: "nav.projects", img: productImages.vinyl, idx: "03" },
  { to: "/about", key: "nav.about", img: productImages.carpet, idx: "04" },
];

export default function Home() {
  const { t, lang } = useLang();
  return (
    <main className="bg-[#F9F8F6] text-[#1A1A1A] font-body" data-testid="page-home">
      <Navbar />
      <HeroSlider />
      <StatsRibbon />

      {/* Story intro */}
      <section className="py-24 lg:py-32 bg-[#F9F8F6]" data-testid="home-intro-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A5A44] mb-5">
              — {lang === "de" ? "Familienbetrieb seit 1985" : "Family-run since 1985"}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[0.95]">
              {lang === "de"
                ? "Vier Jahrzehnte unter Ihren Füßen."
                : "Four decades beneath your feet."}
            </h2>
          </div>
          <div className="lg:col-span-7 flex flex-col gap-6 text-[#4A4A4A] text-base lg:text-lg leading-relaxed max-w-2xl">
            <p>
              {lang === "de"
                ? "Aus einer kleinen Werkstatt in Mülheim ist über vier Jahrzehnte ein Fachbetrieb gewachsen, der heute das gesamte Ruhrgebiet mit Böden, Beratung und Verlegekunst versorgt — vom Altbau-Eichenparkett bis zum technischen Designboden für Praxen und Cafés."
                : "From a small workshop in Mülheim grew a craft house that today serves the whole Ruhr area with floors, advice and installation artistry — from period-home oak parquet to technical design floors for clinics and cafés."}
            </p>
            <p>
              {lang === "de"
                ? "Wir sind ehrlich beim Aufmaß, präzise bei der Verlegung und ansprechbar lange nach dem letzten Sockelpaneel."
                : "We are honest about measurements, precise in our installation and reachable long after the last skirting board."}
            </p>
            <Link to="/about" className="link-underline inline-flex items-center gap-2 text-sm font-medium pt-2 w-fit" data-testid="home-intro-about-link">
              {lang === "de" ? "Mehr über uns" : "More about us"} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Teaser cards linking to subpages */}
      <section className="pb-24 lg:pb-32 bg-[#F9F8F6]" data-testid="home-teaser-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {TEASER_CARDS.map((c, i) => (
              <motion.div
                key={c.to}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`home-teaser-${c.key.split('.')[1]}`}
              >
                <Link
                  to={c.to}
                  className="group relative block aspect-[3/4] overflow-hidden bg-[#1A1A1A]"
                >
                  <img
                    src={c.img}
                    alt={t(c.key)}
                    className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[1200ms] ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute inset-0 p-6 lg:p-7 flex flex-col justify-between text-white">
                    <span className="text-[10px] uppercase tracking-[0.3em] opacity-80">{c.idx}</span>
                    <div className="flex items-end justify-between gap-3">
                      <span className="font-display text-2xl lg:text-3xl">{t(c.key)}</span>
                      <ArrowRight size={20} className="transition-transform duration-500 group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />
      <Footer />
    </main>
  );
}
